import { useState, useEffect, useRef } from "react";
import { Divider, Flex, Typography } from "antd";
import { operadoresSeleccionados, type DashboardPeriod } from "./utils";
import { useGetIdentity } from "@refinedev/core";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { axiosInstance } from "@/utilities";
import { API_URL_STORES, API_URL_USERS } from "@/services/XCONS_ENV";
import { fetchDashboardStores } from "./api/dashboard.api";
import { useDashboardConfig } from "./hooks/use-dashboard-config";
import { MarcadorDeFrescura } from "./components/widget-state";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { StoreFilter } from "@/components/select-store-filter";
import type { User } from "@/types/schema/schema.types";

import { ParaHacerAhora } from "./components/para-hacer-ahora";
import { MisClientes } from "./components/mis-clientes";
import { MetricsPanel } from "./components/metrics-panel";

dayjs.locale("es");

const { Title, Text } = Typography;

export type { DashboardPeriod };

export const DashboardPage = () => {
  const [stores, setStores] = useState<any[]>([]);
  const [userFullName, setUserFullName] = useState("");
  const { data: user } = useGetIdentity<User>();
  const u = user as any;

  // La config de la home se pide UNA vez, acá, y se comparte con el panel: el filtro de stores vive
  // en este componente y escribe en la misma configuración que los widgets.
  const configuracion = useDashboardConfig(u?.id);

  // Mismo cómputo que hace `MetricsPanel` internamente para su panel de filtros — se repite acá
  // (en vez de subirlo como estado compartido) porque `MisClientes` es hermano de `MetricsPanel`,
  // no hijo, y necesita el mismo valor para filtrar sus propios pedidos.
  const myId = u?.id ? String(u.id) : null;
  const operatorsGuardados = configuracion.config?.filters?.operators ?? [];
  const selectedOperatorIds = operadoresSeleccionados(operatorsGuardados, myId);
  // La selección también recorta Tareas pendientes (xcons-docs#310). La agenda ya es la propia;
  // las conversaciones abiertas aceptan UN participante en la API: el mío si estoy entre los
  // elegidos, si no el único operador elegido. Con varios operadores sin incluirme, se muestran
  // todas las del store.
  const operadorDeTareas =
    myId && selectedOperatorIds.includes(myId)
      ? myId
      : selectedOperatorIds.length === 1
        ? selectedOperatorIds[0]
        : null;
  const [frescura, setFrescura] = useState<{
    calculadoHasta: string | null;
    nuncaCalculado: boolean;
  } | null>(null);

  // El filtro del panel se alimenta de los stores que aplican AL DASHBOARD —la unión de los
  // alcances de presupuesto, pedido y contacto—, no de `/stores`, que responde otra pregunta:
  // qué stores ADMINISTRA el operador (`store_permission`). Eran dos conjuntos distintos, así que
  // el filtro podía ofrecer stores sin datos y esconder stores con datos.
  //
  // Se sigue pidiendo `/stores` porque de ahí salen el nombre y el `store_id` que necesitan el
  // selector y el botón de "nuevo presupuesto"; lo que cambia es CUÁLES se muestran.
  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API_URL_STORES}?includeSellerLinked=true`),
      fetchDashboardStores(),
    ])
      .then(([res, alcance]) => {
        const permitidos = new Set([...alcance.storeCodes, ...alcance.sellerCodes]);
        const todos = res.data.data ?? [];
        setStores(todos.filter((s: any) => permitidos.has(s.code)));
      })
      .catch(console.error);
  }, []);

  // ── El filtro de stores ────────────────────────────────────────────────────
  //
  // La fuente de verdad pasa a ser la config del operador. `SelectedStoresContext` sigue existiendo
  // porque lo leen otros componentes de la home (Mis clientes, Tareas pendientes) y las grillas del
  // resto del admin: acá se lo HIDRATA desde la config y se lo mantiene en sincro, en vez de
  // dejarlo como dueño del dato en `localStorage`.
  const { selectedStores, addStore, removeStore, clearStores } = useSelectedStores();
  const hidratado = useRef(false);

  useEffect(() => {
    if (hidratado.current || !configuracion.cargada || !stores.length) return;
    hidratado.current = true;

    const guardados = configuracion.config?.filters?.stores ?? [];
    // Un store guardado al que el operador ya no tiene acceso simplemente no está en `stores`, y se
    // descarta acá sin error: perder un permiso no puede romperle la home a nadie.
    const validos = stores.filter((s: any) => guardados.includes(s.code));

    // Si no hay nada guardado y el operador sólo tiene un store, se lo preseleccionamos: no tiene
    // entre qué elegir, así que no tiene sentido arrancar en el placeholder de "elegí un store".
    // Single-select (xcons-docs#309): de una config vieja con varios stores se conserva el primero.
    const aPreseleccionar = (
      guardados.length === 0 && stores.length === 1 ? stores : validos
    ).slice(0, 1);

    clearStores();
    for (const s of aPreseleccionar) {
      addStore({ code: s.code, name: s.name, seller_id: s.store_id ?? s.storeId });
    }
  }, [configuracion.cargada, stores.length]);

  // De vuelta: lo que el operador elige en el filtro se persiste en su config.
  useEffect(() => {
    if (!hidratado.current || !configuracion.config) return;
    const actuales = selectedStores.map((s) => s.code).sort();
    const guardados = [...(configuracion.config.filters?.stores ?? [])].sort();
    if (actuales.join(",") === guardados.join(",")) return;
    configuracion.actualizar({
      filters: { ...configuracion.config.filters, stores: actuales },
    });
  }, [selectedStores]);

  useEffect(() => {
    if (!u?.id) return;
    axiosInstance
      .get(`${API_URL_USERS}/${u.id}`)
      .then(({ data }) => {
        const name = [data.firstname, data.lastname].filter(Boolean).join(" ") || data.name || "";
        if (name) setUserFullName(name);
      })
      .catch(() => {});
  }, [u?.id]);

  const fullName = userFullName || [u?.firstname, u?.lastname].filter(Boolean).join(" ") || u?.name || "";

  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const timer = setInterval(() => setNow(dayjs()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const dateStr = now.format("dddd DD/MM/YY - HH:mm [hs]");
  const formattedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

  return (
    <div style={{ paddingBottom: 24, minHeight: "100%" }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
        <Flex align="center" gap={12}>
          <Title level={4} style={{ margin: 0 }}>
            👋 Hola, {fullName}
          </Title>
          {/* Selección única (xcons-docs#309): el panel se lee por un store a la vez. */}
          <StoreFilter stores={stores} single />
        </Flex>
        <Flex vertical align="end" gap={2}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formattedDate}
          </Text>
          {frescura && (
            <MarcadorDeFrescura
              calculadoHasta={frescura.calculadoHasta}
              nuncaCalculado={frescura.nuncaCalculado}
            />
          )}
        </Flex>
      </Flex>

      {/* Metrics panel — period tabs, operator selector, metric cards */}
      <MetricsPanel stores={stores} configuracion={configuracion} onFrescura={setFrescura} />

      <Divider style={{ margin: "16px 0" }} />

      {/* Bottom row — tasks (flex:2) + clients (flex:1) */}
      <Flex gap={16} align="stretch">
        <div style={{ flex: 2, minWidth: 0 }}>
          <Text strong style={{ fontSize: 16, display: "block", marginBottom: 8 }}>Tareas pendientes</Text>
          <ParaHacerAhora operatorId={operadorDeTareas} period="day" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 16, display: "block", marginBottom: 8 }}>Clientes</Text>
          <div style={{ height: 400, overflow: "hidden" }}>
            <MisClientes stores={stores} selectedOperatorIds={selectedOperatorIds} />
          </div>
        </div>
      </Flex>
    </div>
  );
};
